import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, TrendingUp, Zap, Sparkles, Check, ChevronRight, Briefcase } from "lucide-react";
import { Button } from "@/components/ui/button";
import MtgLeadForm from "@/components/MtgLeadForm";
import { TEASER_ANNUAL_FROM, TEASER_MONTHLY_FROM } from "@/lib/offers";

const LOGO = "/brand/mtg_logo.svg";
const LOGO_WHITE = "/brand/mtg_logo_white.svg";
const SYMBOL = "/brand/mtg_logo_compact.svg";

function Nav() {
  return (
    <nav className="w-full flex items-center justify-between px-6 md:px-12 py-5 relative z-10">
      <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
        <img src={LOGO} alt="MonTempsGagné" className="h-9 md:h-10" />
      </Link>
      <div className="flex items-center gap-3 md:gap-5">
        <Link to="/tarifs" className="text-sm font-semibold text-[#16324F] hover:text-[#19C3B3] transition-base" data-testid="nav-tarifs">Tarifs</Link>
        <Link to="/demo" className="text-sm font-semibold text-[#16324F] hover:text-[#19C3B3] transition-base" data-testid="nav-demo">Démo</Link>
      </div>
    </nav>
  );
}

export default function Landing() {
  const HERO_SERVICES = [
    { key: "duplicate", label: "Faire un double de clé", price: 189 },
    { key: "lost_all", label: "Toutes les clés sont perdues", price: 289 },
    { key: "broken", label: "Clé cassée ou endommagée", price: 149 },
  ];
  const [heroPick, setHeroPick] = useState("duplicate");
  const current = HERO_SERVICES.find((s) => s.key === heroPick) || HERO_SERVICES[0];

  return (
    <div className="min-h-screen bg-white">
      {/* HERO */}
      <div className="relative overflow-hidden montempsgagne-hero-ambient kf-mesh">
        <Nav />
        <section className="relative z-10 px-6 md:px-12 pt-4 md:pt-12 pb-12 md:pb-24 max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-white border border-[#EAF1F7] shadow-sm px-3 py-1 text-xs font-semibold text-[#16324F]">
                <span className="w-2 h-2 rounded-full bg-[#19C3B3]"></span>
                Pour les professionnels de l&apos;automobile
              </span>
              <h1 className="mt-5 md:mt-6 font-display text-[2.25rem] leading-[1.05] sm:text-6xl lg:text-7xl sm:leading-[0.98] font-extrabold text-[#16324F] tracking-tight">
                Des devis <span className="text-[#19C3B3]">automatiques</span>.<br />
                Plus de temps pour <span className="bg-gradient-to-r from-[#FF7A59] to-[#FFB199] bg-clip-text text-transparent">intervenir</span>.
              </h1>
              <p className="mt-4 md:mt-6 text-base md:text-lg text-slate-600 max-w-xl leading-relaxed">
                Moins de temps à traiter les demandes, plus de temps pour votre métier. MonTempsGagné automatise les demandes de devis selon vos prestations et vos tarifs. Découvrez son fonctionnement avec notre démonstration dédiée à la serrurerie automobile.
              </p>
              <div className="mt-6 md:mt-8 flex flex-wrap items-center gap-3 md:gap-4">
                <div className="flex flex-col items-start">
                  <Link to="/demo" data-testid="hero-cta-demo">
                    <div className="relative inline-block montempsgagne-cta-glow">
                      <Button size="lg" className="relative rounded-full bg-[#16324F] hover:bg-[#0F2438] text-white px-7 h-12 text-base font-semibold shadow-lg shadow-[#16324F]/20">
                        Voir la démo <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </Link>
                  <span className="mt-1.5 text-xs font-semibold text-slate-500">Démo : serrurerie automobile</span>
                </div>
                <a href="#evaluer" data-testid="hero-cta-evaluate">
                  <Button size="lg" variant="outline" className="rounded-full border-2 border-[#16324F]/20 text-[#16324F] px-7 h-12 text-base font-semibold hover:bg-white hover:border-[#16324F]/40">
                    Évaluer mon projet
                  </Button>
                </a>
              </div>
              <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#16324F]/70">
                <Sparkles className="w-4 h-4 text-[#FF7A59]" /> Des devis automatiques. Plus de temps pour votre métier.
              </div>
            </div>

            {/* Interactive demo card */}
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-[#19C3B3]/25 via-transparent to-[#FF7A59]/20 rounded-[32px] blur-2xl"></div>
              <div className="relative bg-white rounded-3xl border border-slate-100 shadow-[0_30px_80px_-40px_rgba(16,50,79,0.35)] p-6 md:p-8" data-testid="hero-preview">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-xs font-semibold text-slate-400">Étape 3 sur 5</div>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#16324F]/5 text-[#16324F] text-[10px] font-bold uppercase tracking-widest px-2.5 py-1">
                    Exemple : serrurerie automobile
                  </span>
                </div>
                <div className="kf-progress-track mt-2"><div className="kf-progress-fill" style={{width: "60%"}}></div></div>
                <h3 className="mt-6 font-display text-2xl font-bold text-[#16324F]">De quoi avez-vous besoin ?</h3>
                <div className="mt-4 grid gap-2" role="radiogroup" aria-label="Aperçu interactif">
                  {HERO_SERVICES.map((s) => {
                    const selected = heroPick === s.key;
                    return (
                      <button
                        key={s.key}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => setHeroPick(s.key)}
                        className="kf-select-card !p-3 flex items-center justify-between text-left w-full"
                        data-selected={selected ? "true" : "false"}
                        data-testid={`hero-service-${s.key}`}
                      >
                        <span className="text-sm font-semibold text-[#16324F]">{s.label}</span>
                        <span
                          className={`inline-flex items-center justify-center w-5 h-5 rounded-full border-2 transition-all duration-200 ${
                            selected ? "bg-[#19C3B3] border-[#19C3B3] text-white scale-100" : "border-slate-200 text-transparent scale-90"
                          }`}
                          aria-hidden="true"
                        >
                          <Check className="w-3 h-3" strokeWidth={3} />
                        </span>
                      </button>
                    );
                  })}
                </div>
                <div className="mt-6 rounded-2xl bg-gradient-to-br from-[#EAF1F7] to-white p-4 flex items-center justify-between border border-[#EAF1F7]">
                  <span className="text-sm text-slate-600">Devis estimé</span>
                  <span
                    key={current.key}
                    className="font-display font-extrabold text-2xl text-[#16324F]"
                    data-testid="hero-price"
                    style={{ animation: "kfPricePop 320ms cubic-bezier(.2,.7,.2,1)" }}
                  >
                    {current.price} € TTC
                  </span>
                </div>
                <p className="mt-3 text-[11px] text-slate-400 italic">Aperçu simplifié · tarifs configurables par chaque serrurier.</p>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* LAUNCH OFFER BANNER */}
      <section className="px-6 md:px-12 pt-10 md:pt-12 max-w-6xl mx-auto w-full" data-testid="home-launch-offer">
        <div className="rounded-3xl border border-[#19C3B3]/30 bg-[#19C3B3]/8 p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
          <div className="flex-1">
            <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-[#0E5A56]">
              <Sparkles className="w-3.5 h-3.5 text-[#19C3B3]" /> Offre de lancement
            </div>
            <h2 className="mt-3 font-display text-2xl md:text-3xl font-extrabold text-[#16324F]">Testez MonTempsGagné pendant 3 mois gratuitement</h2>
            <p className="mt-2 text-sm md:text-base text-slate-600 leading-relaxed max-w-2xl">
              Profitez de 3 mois offerts sur les forfaits Essentiel et Pro. Jusqu&apos;à 6 mois offerts sous conditions.
            </p>
            <p className="mt-1 text-xs text-slate-400">Nenzio non inclus.</p>
          </div>
          <Link to="/tarifs" className="w-full md:w-auto md:shrink-0" data-testid="home-launch-cta">
            <Button className="w-full md:w-auto h-12 px-6 md:px-8 rounded-full bg-[#16324F] text-white hover:bg-[#1d4368] text-sm md:text-[15px] font-semibold">
              Découvrir les offres <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* TRADES BLOCK - white */}
      <section className="bg-white py-14 md:py-20 px-6 md:px-12" data-testid="section-trades">
        <div className="max-w-6xl mx-auto grid md:grid-cols-[1.1fr_1fr] gap-10 md:gap-16 items-start">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-[#19C3B3]">Votre métier, votre outil</div>
            <h2 className="mt-2 font-display text-3xl md:text-5xl font-extrabold text-[#16324F] tracking-tight">Votre métier, vos prestations, vos tarifs.</h2>
            <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed">
              Serrurerie automobile, nettoyage, detailing, pose de films teintés… Les demandes diffèrent, mais le besoin reste le même : recueillir les bonnes informations et gagner du temps dans la préparation des devis.
            </p>
            <div className="mt-5 inline-flex items-start gap-2 rounded-2xl bg-[#EAF1F7] border border-[#EAF1F7] px-4 py-3 text-sm text-[#16324F]">
              <Briefcase className="w-4 h-4 mt-0.5 text-[#19C3B3] shrink-0" />
              <span><span className="font-bold">Première démonstration disponible : serrurerie automobile.</span> L&apos;adaptation à d&apos;autres métiers se construit progressivement.</span>
            </div>
          </div>

          <div>
            <div className="rounded-3xl border border-slate-100 bg-[#F6F9FC] p-6 md:p-8">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Exemples de métiers</div>
              <ul className="mt-4 grid gap-2 text-sm font-semibold text-[#16324F]">
                {["Serrurerie automobile", "Nettoyage / detailing automobile", "Pose de films teintés", "Garage / entretien automobile"].map((t) => (
                  <li key={t} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#19C3B3]"></span>{t}
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-sm text-slate-600 leading-relaxed">
                Un autre métier en tête ? Voyons ensemble ce que nous pouvons simplifier dans votre quotidien.
              </p>
              <a href="#evaluer" data-testid="trades-cta-evaluate" className="mt-4 inline-block">
                <Button className="rounded-full bg-[#16324F] hover:bg-[#0F2438] text-white h-11 px-5 font-semibold">
                  Évaluer mon projet <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS - white */}
      <section className="bg-white py-14 md:py-24 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-widest text-[#19C3B3]">Comment ça marche</div>
            <h2 className="mt-2 font-display text-3xl md:text-5xl font-extrabold text-[#16324F] tracking-tight">Un parcours guidé, entièrement automatisé.</h2>
          </div>

          <div className="mt-10 md:mt-16 montempsgagne-steps grid md:grid-cols-4 gap-6 md:gap-4">
            {[
              { n: "1", t: "Le client décrit son besoin", d: "Véhicule, prestation, localisation et questions métier.", tone: "#19C3B3" },
              { n: "2", t: "MonTempsGagné génère le devis", d: "Les règles tarifaires du serrurier sont appliquées automatiquement.", tone: "#19C3B3" },
              { n: "3", t: "Le client réserve", d: "Il choisit directement un créneau disponible.", tone: "#FF7A59" },
              { n: "4", t: "Le serrurier récupère du temps", d: "Demande, devis et réservation arrivent déjà traités.", tone: "#FF7A59" },
            ].map((s) => (
              <div key={s.n} className="relative z-10 text-center md:text-left" data-testid={`workflow-step-${s.n}`}>
                <div
                  className="mx-auto md:mx-0 w-14 h-14 rounded-full flex items-center justify-center font-display font-extrabold text-lg shadow-lg text-white"
                  style={{ background: s.tone }}
                >
                  {s.n}
                </div>
                <div className="mt-5 font-display font-bold text-lg text-[#16324F]">{s.t}</div>
                <div className="mt-1.5 text-sm text-slate-600 leading-relaxed">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS - soft bg */}
      <section className="montempsgagne-bg-soft py-20 md:py-24 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-widest text-[#FF7A59]">Ce que MonTempsGagné change</div>
            <h2 className="mt-2 font-display text-3xl md:text-5xl font-extrabold text-[#16324F] tracking-tight">Un même outil, deux bénéfices concrets.</h2>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {/* Pro */}
            <div className="relative bg-white rounded-3xl p-8 border border-slate-100 shadow-[0_20px_60px_-40px_rgba(16,50,79,0.4)] overflow-hidden">
              <div className="absolute top-0 left-0 w-1.5 h-full bg-[#19C3B3]"></div>
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#19C3B3]">
                <Zap className="w-4 h-4" /> Professionnel
              </div>
              <h3 className="mt-3 font-display text-2xl md:text-3xl font-extrabold text-[#16324F] leading-tight">Moins de devis à traiter manuellement.<br />Plus de temps pour intervenir.</h3>
              <ul className="mt-6 space-y-3">
                {[
                  "Moins d'appels et de rappels",
                  "Demandes mieux qualifiées",
                  "Devis envoyés immédiatement",
                  "Demandes traitées 24h/24",
                  "Meilleure conversion des demandes",
                ].map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
                    <span className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-[#19C3B3]/15 text-[#19C3B3] flex items-center justify-center">
                      <Check className="w-3 h-3" strokeWidth={3} />
                    </span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Client */}
            <div className="relative bg-white rounded-3xl p-8 border border-slate-100 shadow-[0_20px_60px_-40px_rgba(16,50,79,0.4)] overflow-hidden">
              <div className="absolute top-0 left-0 w-1.5 h-full bg-[#FF7A59]"></div>
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#FF7A59]">
                <Sparkles className="w-4 h-4" /> Client
              </div>
              <h3 className="mt-3 font-display text-2xl md:text-3xl font-extrabold text-[#16324F] leading-tight">Un prix clair, une réservation en quelques clics.</h3>
              <ul className="mt-6 space-y-3">
                {[
                  "Obtenir son devis rapidement",
                  "Ne pas devoir appeler",
                  "Faire sa demande 24h/24",
                  "Réserver immédiatement après le devis",
                  "Parcours simple et transparent",
                ].map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
                    <span className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-[#FF7A59]/15 text-[#FF7A59] flex items-center justify-center">
                      <Check className="w-3 h-3" strokeWidth={3} />
                    </span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CLAVEO PILOT - navy */}
      <section className="montempsgagne-bg-navy py-16 md:py-28 px-6 md:px-12 relative overflow-hidden">
        <div className="max-w-6xl mx-auto grid md:grid-cols-[1.4fr_1fr] gap-10 md:gap-12 items-center relative z-10">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-[#FF7A59] text-white text-[10px] font-bold tracking-widest px-3 py-1 uppercase">
              <span className="w-1.5 h-1.5 rounded-full bg-white"></span> Entreprise pilote
            </span>
            <h2 className="mt-5 font-display text-4xl md:text-5xl font-extrabold text-white leading-[1.05] tracking-tight">
              Claveo, <span className="text-[#19C3B3]">entreprise pilote</span>.
            </h2>
            <p className="mt-5 text-base md:text-lg text-white/80 max-w-2xl leading-relaxed">
              Chez Claveo, entreprise de serrurerie automobile, un appel dure entre 2 et 4 minutes selon les observations de l&apos;équipe. Le pilote MonTempsGagné doit permettre d&apos;automatiser les demandes standards selon ses règles tarifaires et de mesurer le temps gagné.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/quote/claveo" data-testid="pilot-cta-claveo">
                <Button size="lg" className="rounded-full bg-[#19C3B3] hover:bg-[#0FAB9B] text-[#16324F] px-7 h-12 text-base font-bold shadow-lg shadow-[#19C3B3]/25">
                  Tester MonTempsGagné avec Claveo <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
          <div className="relative hidden md:block">
            <div className="absolute inset-0 bg-gradient-to-br from-[#19C3B3]/20 to-[#FF7A59]/10 rounded-[32px] blur-3xl"></div>
            <div className="relative bg-white/5 backdrop-blur-sm border border-white/15 rounded-3xl p-8">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#19C3B3]">Aperçu de l'espace Claveo</div>
              <div className="mt-4 font-display font-extrabold text-3xl text-white">Claveo</div>
              <div className="mt-1 text-sm text-white/60">claveo.fr — Serrurerie automobile</div>
              <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-xl bg-white/5 p-3">
                  <div className="text-white/50 text-[10px] uppercase tracking-wider">Tarifs</div>
                  <div className="mt-1 font-bold text-white">Configurés</div>
                </div>
                <div className="rounded-xl bg-white/5 p-3">
                  <div className="text-white/50 text-[10px] uppercase tracking-wider">Disponibilités</div>
                  <div className="mt-1 font-bold text-white">Temps réel</div>
                </div>
                <div className="rounded-xl bg-white/5 p-3">
                  <div className="text-white/50 text-[10px] uppercase tracking-wider">Zones</div>
                  <div className="mt-1 font-bold text-white">Locales</div>
                </div>
                <div className="rounded-xl bg-white/5 p-3">
                  <div className="text-white/50 text-[10px] uppercase tracking-wider">Devis</div>
                  <div className="mt-1 font-bold text-white">Automatiques</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PILOT METRICS - light with symbol */}
      <section className="bg-white py-16 md:py-24 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="font-display text-3xl md:text-5xl font-extrabold text-[#16324F] tracking-tight">Des minutes gagnées, des journées récupérées.</h2>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#16324F] text-white text-[10px] font-bold tracking-widest px-2.5 py-1 uppercase">
              <span className="w-1.5 h-1.5 rounded-full bg-[#19C3B3]"></span>
              Pilote terrain
            </span>
          </div>

          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {[
              {
                label: "Temps actuel observé",
                value: "2 à 4 min",
                unit: "/ appel",
                detail: "Durée observée par Claveo.",
                icon: Clock,
                tone: "#19C3B3",
                highlight: false,
              },
              {
                label: "Objectif du pilote",
                value: "10",
                unit: "demandes / jour",
                detail: "À automatiser avec MonTempsGagné.",
                icon: Zap,
                tone: "#19C3B3",
                highlight: false,
              },
              {
                label: "Temps potentiellement libéré",
                value: "Jusqu'à 18",
                unit: "jours / an*",
                detail: "Équivalent en journées de travail de 8 heures.",
                icon: TrendingUp,
                tone: "#FF7A59",
                highlight: true,
              },
            ].map((it, i) => (
              <div
                key={i}
                className={`relative rounded-3xl p-8 border ${
                  it.highlight
                    ? "bg-[#16324F] border-transparent text-white shadow-[0_30px_80px_-30px_rgba(255,122,89,0.55)] md:-mt-2"
                    : "bg-white border-slate-100 shadow-[0_20px_60px_-45px_rgba(16,50,79,0.35)]"
                }`}
                data-testid={`pilot-metric-${i}`}
              >
                {it.highlight && (
                  <span className="absolute -top-3 left-6 inline-flex items-center gap-1.5 rounded-full bg-[#FF7A59] text-white text-[10px] font-bold tracking-widest px-2.5 py-1 uppercase">
                    Projection
                  </span>
                )}
                <it.icon className="w-6 h-6" style={{ color: it.highlight ? "#FF7A59" : it.tone }} />
                <div className={`mt-3 text-[11px] font-bold uppercase tracking-widest ${it.highlight ? "text-white/70" : "text-slate-500"}`}>{it.label}</div>
                <div className={`mt-2 font-display font-extrabold leading-none ${it.highlight ? "text-white" : "text-[#16324F]"}`}>
                  <span className="text-4xl md:text-5xl">{it.value}</span>
                  <span className={`text-xl md:text-2xl ml-2 font-bold ${it.highlight ? "text-white/70" : "text-slate-500"}`}>{it.unit}</span>
                </div>
                <div className={`mt-3 text-sm ${it.highlight ? "text-white/75" : "text-slate-600"}`}>{it.detail}</div>
              </div>
            ))}
          </div>

          <p className="mt-8 text-xs text-slate-500 max-w-3xl">
            *Estimation sur 10 appels évités par jour, de 2 à 4 minutes chacun, pendant 220 jours par an. Équivalent en journées de 8 heures.
          </p>
          <div className="mt-4 text-xs text-slate-500">
            <span className="font-semibold text-[#16324F]">KPI suivis :</span>{" "}
            taux d&apos;automatisation · temps économisé · appels et rappels évités · taux devis → réservation
          </div>
        </div>
      </section>

      {/* MTG LEAD FORM - soft bg */}
      <section id="evaluer" className="montempsgagne-bg-soft py-16 md:py-24 px-6 md:px-12 scroll-mt-16" data-testid="section-evaluate">
        <div className="max-w-5xl mx-auto grid md:grid-cols-[1fr_1.2fr] gap-10 md:gap-14 items-start">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-[#FF7A59]">Parcours dédié aux pros</div>
            <h2 className="mt-2 font-display text-3xl md:text-5xl font-extrabold text-[#16324F] tracking-tight">Combien de temps pourriez-vous gagner ?</h2>
            <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed">
              Décrivez votre activité et les demandes que vous souhaitez automatiser. Ces informations nous permettront de vous orienter vers une offre adaptée.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-slate-600">
              {["5 questions, environ 2 minutes.", "Réponses conservées entre chaque étape.", "Aucun compte requis."].map((b) => (
                <li key={b} className="flex items-start gap-2">
                  <span className="mt-0.5 shrink-0 w-4 h-4 rounded-full bg-[#19C3B3]/15 text-[#19C3B3] flex items-center justify-center">
                    <Check className="w-2.5 h-2.5" strokeWidth={3} />
                  </span>
                  {b}
                </li>
              ))}
            </ul>
          </div>
          <MtgLeadForm />
        </div>
      </section>

      {/* PRICING TEASER */}
      <section className="bg-white py-14 md:py-20 px-6 md:px-12" data-testid="section-pricing-teaser">
        <div className="max-w-5xl mx-auto rounded-3xl montempsgagne-bg-soft p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h2 className="font-display text-2xl md:text-3xl font-extrabold text-[#16324F]">Une solution pensée pour vous faire gagner du temps.</h2>
            <p className="mt-3 text-slate-600 max-w-xl">Automatisez les demandes, les devis et les rendez-vous de votre activité automobile.</p>
            <div className="mt-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="font-display text-xl font-extrabold text-[#16324F]" data-testid="teaser-price-monthly">{TEASER_MONTHLY_FROM}</span>
              <span className="text-sm font-semibold text-slate-400">ou</span>
              <span className="font-display text-xl font-extrabold text-[#16324F]" data-testid="teaser-price">{TEASER_ANNUAL_FROM}</span>
            </div>
            <p className="mt-1 text-xs text-slate-400">Bientôt : Nenzio, le copilote IA de MonTempsGagné.</p>
          </div>
          <Link to="/tarifs" data-testid="teaser-cta-tarifs" className="shrink-0">
            <Button className="h-12 px-8 rounded-full bg-[#16324F] text-white hover:bg-[#1d4368] text-base font-semibold">Voir les tarifs <ArrowRight className="ml-2 h-4 w-4" /></Button>
          </Link>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="montempsgagne-bg-navy py-14 px-6 md:px-12">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <img src={LOGO_WHITE} alt="MonTempsGagné" className="h-9" />
            <div className="h-8 w-px bg-white/20 hidden md:block"></div>
            <div className="text-sm text-white/80">Des devis automatiques. Plus de temps pour votre métier.</div>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
            <Link to="/tarifs" className="text-white/70 hover:text-white transition-colors" data-testid="footer-tarifs">Tarifs</Link>
            <Link to="/demo" className="text-white/70 hover:text-white transition-colors">Démo</Link>
            <Link to="/mentions-legales" className="text-white/70 hover:text-white transition-colors" data-testid="footer-legal">Mentions légales</Link>
            <Link to="/confidentialite" className="text-white/70 hover:text-white transition-colors" data-testid="footer-privacy">Confidentialité</Link>
            <Link to="/connexion" className="text-white/70 hover:text-white transition-colors" data-testid="footer-pro-login">Espace professionnel</Link>
            <span className="text-white/40">© {new Date().getFullYear()} MonTempsGagné</span>
          </div>
        </div>
        <div className="max-w-6xl mx-auto mt-6 text-xs text-white/45">
          Réalisé avec cœur par <a href="https://flamingops.fr" rel="noopener noreferrer" className="text-white/70 hover:text-white underline underline-offset-2" data-testid="footer-flamingops">FlamingOps</a>
        </div>
      </footer>
    </div>
  );
}
